import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
} from "@mui/material";

function AddressSuggestionDialog({
  open,
  onClose,
  suggestedAddress,
  suggestions,
  selectedSuggestion,
  onSelectSuggestion,
  onKeepCustom,
  onAcceptSuggestion,
  isCustomSelect,
}) {
  const addressLabel =
    suggestedAddress ||
    selectedSuggestion?.address ||
    suggestions?.[0]?.address ||
    "this address";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="suggestion-dialog-title"
      aria-describedby="suggestion-dialog-description"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: "var(--theme-bg-header)",
          border: "1px solid rgba(65, 58, 54, 0.12)",
          boxShadow: "0 20px 45px rgba(65, 58, 54, 0.18)",
          minWidth: { xs: "92vw", sm: isCustomSelect ? 360 : 420 },
          maxWidth: isCustomSelect ? 440 : undefined,
        },
      }}
    >
      { (
        <DialogTitle
          id="suggestion-dialog-title"
          sx={{
            color: "var(--theme-color)",
            fontWeight: 800,
            letterSpacing: "0.2px",
            pb: 0.5,
          }}
        >
           { !isCustomSelect ?  `Did you mean ${addressLabel}?` : '' }
        </DialogTitle>
      )}
      <DialogContent sx={{ pt: 0.5, textAlign: isCustomSelect ? "center" : "left" }}>
        <Typography
          id="suggestion-dialog-description"
          sx={{ color: "var(--theme-color)", opacity: 0.85 }}
        >
          {isCustomSelect ? (
            <>
              You have selected a location very near{" "}
              <strong>{addressLabel}</strong>. Do you want to use that address?
            </>
          ) : (
            "You clicked a custom spot. We found nearby addresses - pick one from the list if you'd like to use it instead."
          )}
        </Typography>
        {!isCustomSelect && (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Select
              id="nearby-addresses"
              value={
                selectedSuggestion?.placeId ||
                selectedSuggestion?.address ||
                ""
              }
              displayEmpty
              onChange={(event) => {
                const nextValue = event.target.value;
                const match = suggestions.find((option) =>
                  option?.placeId
                    ? option.placeId === nextValue
                    : option?.address === nextValue
                );
                onSelectSuggestion(match || null);
              }}
              MenuProps={{
                disablePortal: true,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                PaperProps: {
                  sx: {
                    mt: "2px",
                    border: "1px solid var(--theme-color)",
                    borderRadius: 0,
                    boxShadow: "none",
                    overflowX: "hidden",
                    maxWidth: "min-content",
                    maxHeight: "calc(100vh - 500px)",
                    "& .MuiMenu-list li": {
                      whiteSpace: "normal",
                      fontSize: "0.875rem",
                      "&:hover": {
                        backgroundColor: "var(--theme-bg-clr)",
                      },
                    },
                  },
                },
                PopperProps: {
                  modifiers: [
                    {
                      name: "setMaxWidth",
                      enabled: true,
                      phase: "beforeWrite",
                      requires: ["computeStyles"],
                      fn: ({ state }) => {
                        const parentWidth = state.rects.reference.width;
                        state.styles.popper.maxWidth = `${parentWidth}px`;
                      },
                    },
                  ],
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  minHeight: 38,
                },
                "& .MuiSelect-select": {
                  padding: "6px 10px",
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: "var(--theme-color)",
                  borderRadius: "1px",
                  backgroundColor: "var(--theme-btn-clr)",
                },
                "& .MuiSelect-icon": {
                  color: "var(--theme-color)",
                  fontSize: "2rem",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(65, 58, 54, 0.25)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--theme-color)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--theme-color)",
                },
                "& .MuiMenuItem-root.Mui-selected": {
                  backgroundColor: "transparent",
                  fontWeight: 700,
                },
              }}
            >
              {suggestions.map((option) => {
                const value = option?.placeId || option?.address;
                return (
                  <MenuItem key={value} value={value}>
                    {option?.address || ""}
                  </MenuItem>
                );
              })}
              {suggestions.length === 0 && (
                <MenuItem value="" disabled>
                  No nearby addresses found
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          gap: isCustomSelect ? 1 : 1.5,
          flexDirection: isCustomSelect ? "column-reverse" : "row",
          alignItems: isCustomSelect ? "stretch" : "center",
        }}
      >
        <Button
          onClick={onKeepCustom}
          variant="outlined"
          fullWidth={isCustomSelect}
          sx={{
            flex: isCustomSelect ? undefined : 1,
            borderColor: "var(--theme-color)",
            color: "var(--theme-color)",
            backgroundColor: "var(--theme-btn-clr)",
            textTransform: "unset",
            fontWeight: 700,
            minHeight: isCustomSelect ? 44 : undefined,
            height: "auto",
            whiteSpace: "normal",
            textAlign: "center",
            lineHeight: 1.25,
            padding: isCustomSelect ? "10px 16px" : undefined,
            "&:hover": {
              borderColor: "var(--theme-color)",
              backgroundColor: "rgba(65, 58, 54, 0.06)",
            },
          }}
        >
       {isCustomSelect ?  "Don't accept suggestion - add custom location" : "Don't accept suggestion"}
        </Button>
        <Button
          onClick={onAcceptSuggestion}
          variant="contained"
          autoFocus
          fullWidth={isCustomSelect}
          sx={{
            flex: isCustomSelect ? undefined : 1,
            backgroundColor: "var(--theme-color)",
            color: "var(--theme-btn-clr)",
            textTransform: "unset",
            fontWeight: 800,
            boxShadow: "none",
            minHeight: isCustomSelect ? 46 : undefined,
            height: "auto",
            whiteSpace: "normal",
            textAlign: "center",
            lineHeight: 1.2,
            marginLeft: "1px",
            padding: isCustomSelect ? "10px 16px" : undefined,
            "&:hover": {
              backgroundColor: "var(--theme-color)",
              boxShadow: "0 8px 20px rgba(65, 58, 54, 0.2)",
            },
          }}
        >
          Use suggestion
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddressSuggestionDialog;
